import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { HousingBackendService } from 'src/app/services/housing-backend.service';
import { ChangeLog } from '../models/change-log.model';
import {
	HousingSearch,
	HousingSearchResponse,
} from '../models/housing-search.model';
import { HousingUnit } from '../models/housing-unit.model';
import { UserDropDown } from '../models/user-drop-down.model';
import { Logout } from './auth.action';
import {
	ActivateHousingUnit,
	AddHousingUnit,
	ArchiveHousingUnit,
	GetHousing,
	GetHousingUnit,
	GetHousingUnitChangeLogs,
	GetTenants,
	HousingSearchToggled,
	SaveHousingUnit,
	SetAddHousingError,
	SetHousingSearchFromForm,
	SetHousingSearchPage,
	SetHousingUnit,
	SetNewHousingUnit,
} from './housing.action';
import { HousingStateModel } from './housing.model';
import { StartLoading, StopLoading } from './main.action';

export const DEFAULT_STATE: HousingStateModel = {
	searchOptions: {
		page: 0,
		currentTenantId: null,
		compound: '',
		unitType: '',
		numberOfBedrooms: null,
		useContractStartDate: false,
		contractStartDateAfter: null,
		contractStartDateBefore: null,
		useContractEndDate: false,
		contractEndDateAfter: null,
		contractEndDateBefore: null,
		includeArchived: false,
	},
	searchOpened: false,
	housingUnits: [],
	count: null,
	page: 0,
	limit: 0,
	tenants: [],
	newHousingUnit: null,
	addHousingError: null,
	selectedHousingUnit: null,
	selectedHousingUnitChangeLogs: null,
};

@State<HousingStateModel>({
	name: 'housing',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class HousingState {
	private housingBackend = inject(HousingBackendService);

	@Selector()
	static housingState(state: HousingStateModel): HousingStateModel {
		return state;
	}

	@Selector()
	static searchOptions(state: HousingStateModel): HousingSearch {
		return state.searchOptions;
	}

	@Selector()
	static searchOpened(state: HousingStateModel): boolean {
		return state.searchOpened;
	}

	@Selector()
	static housingUnits(state: HousingStateModel): HousingUnit[] {
		return state.housingUnits;
	}

	@Selector()
	static count(state: HousingStateModel): number {
		return state.count;
	}

	@Selector()
	static page(state: HousingStateModel): number {
		return state.page;
	}

	@Selector()
	static limit(state: HousingStateModel): number {
		return state.limit;
	}

	@Selector()
	static tenants(state: HousingStateModel): UserDropDown[] {
		return state.tenants;
	}

	@Selector()
	static newHousingUnit(state: HousingStateModel): HousingUnit {
		return state.newHousingUnit;
	}

	@Selector()
	static addHousingError(state: HousingStateModel): string {
		return state.addHousingError;
	}

	@Selector()
	static selectedHousingUnit(state: HousingStateModel): HousingUnit {
		return state.selectedHousingUnit;
	}

	@Selector()
	static selectedHousingUnitChangeLogs(
		state: HousingStateModel
	): ChangeLog[] {
		return state.selectedHousingUnitChangeLogs;
	}

	@Action(GetHousing)
	getHousing(ctx: StateContext<HousingStateModel>, action: GetHousing) {
		ctx.dispatch([new StartLoading()]);
		ctx.patchState({ searchOptions: action.search });
		return this.housingBackend.getHousing(action.search).pipe(
			tap((response: HousingSearchResponse) => {
				ctx.patchState({
					housingUnits: response.housingUnits.map((json) =>
						HousingUnit.fromJSON(json)
					),
					count: response.count,
					page: response.page,
					limit: response.limit,
				});
				ctx.dispatch(new StopLoading());
			}),
			catchError((error) => {
				ctx.dispatch([new StopLoading()]);
				return throwError(() => error);
			})
		);
	}

	@Action(SetHousingSearchPage)
	setHousingSearchPage(
		ctx: StateContext<HousingStateModel>,
		action: SetHousingSearchPage
	) {
		const newSearchOptions = Object.assign(
			Object.create(HousingSearch.prototype),
			ctx.getState().searchOptions,
			{
				page: action.page,
			}
		);
		ctx.patchState({
			searchOptions: newSearchOptions,
		});
	}

	@Action(SetHousingSearchFromForm)
	setHousingSearchFromForm(
		ctx: StateContext<HousingStateModel>,
		action: SetHousingSearchFromForm
	) {
		const newSearchOptions = Object.assign(
			Object.create(HousingSearch.prototype),
			action.search,
			{
				page: ctx.getState().searchOptions
					? ctx.getState().searchOptions.page
					: 0,
			}
		);
		ctx.patchState({
			searchOptions: newSearchOptions,
		});
	}

	@Action(GetTenants)
	getTenants(ctx: StateContext<HousingStateModel>, _action: GetTenants) {
		return this.housingBackend
			.getTenants()
			.pipe(tap((tenants) => ctx.patchState({ tenants: tenants })));
	}

	@Action(HousingSearchToggled)
	housingSearchToggled(
		ctx: StateContext<HousingStateModel>,
		action: HousingSearchToggled
	) {
		ctx.patchState({
			searchOpened: action.opened,
		});
	}

	@Action(SetNewHousingUnit)
	setNewHousingUnit(
		ctx: StateContext<HousingStateModel>,
		action: SetNewHousingUnit
	) {
		ctx.patchState({
			newHousingUnit: action.newHousingUnit,
		});
	}

	@Action(SetAddHousingError)
	setAddHousingError(
		ctx: StateContext<HousingStateModel>,
		action: SetAddHousingError
	) {
		ctx.patchState({
			addHousingError: action.error,
		});
	}

	@Action(AddHousingUnit)
	addHousingUnit(
		ctx: StateContext<HousingStateModel>,
		action: AddHousingUnit
	) {
		ctx.dispatch([new StartLoading()]);
		ctx.patchState({
			newHousingUnit: null,
			addHousingError: null,
		});
		return this.housingBackend.addHousingUnit(action.housingUnit).pipe(
			tap((response) => {
				ctx.dispatch(new StopLoading());
				ctx.patchState({
					newHousingUnit: HousingUnit.fromJSON(response.housingUnit),
					addHousingError: null,
				});
			}),
			catchError((error) => {
				ctx.dispatch(new StopLoading());
				ctx.patchState({
					newHousingUnit: null,
					addHousingError: error,
				});
				return throwError(() => error);
			})
		);
	}

	@Action(GetHousingUnit)
	getHousingUnit(
		ctx: StateContext<HousingStateModel>,
		action: GetHousingUnit
	) {
		ctx.dispatch([new StartLoading()]);
		ctx.patchState({
			selectedHousingUnit: null,
			selectedHousingUnitChangeLogs: null,
		});
		return this.housingBackend.getHousingUnit(action.id).pipe(
			tap((housingUnit) => {
				ctx.patchState({
					selectedHousingUnit: housingUnit,
				});
				ctx.dispatch([new StopLoading()]);
			})
		);
	}

	@Action(GetHousingUnitChangeLogs)
	getHousingUnitChangeLogs(
		ctx: StateContext<HousingStateModel>,
		_action: GetHousingUnitChangeLogs
	) {
		ctx.dispatch([new StartLoading()]);
		ctx.patchState({ selectedHousingUnitChangeLogs: null });
		return ctx.getState().selectedHousingUnit
			? this.housingBackend
					.getHousingUnitChangeLogs(
						ctx.getState().selectedHousingUnit.id
					)
					.pipe(
						tap((changeLogs) => {
							ctx.patchState({
								selectedHousingUnitChangeLogs: changeLogs,
							});
							ctx.dispatch([new StopLoading()]);
						})
					)
			: null;
	}

	@Action(SaveHousingUnit)
	saveHousingUnit(
		ctx: StateContext<HousingStateModel>,
		action: SaveHousingUnit
	) {
		ctx.dispatch(new StartLoading());
		return this.housingBackend.saveHousingUnit(action.housingUnit).pipe(
			tap((housingUnit) => {
				ctx.dispatch(new StopLoading());
				ctx.patchState({
					selectedHousingUnit: housingUnit,
				});
			}),
			catchError((error) => {
				ctx.dispatch([
					new StopLoading(),
					new GetHousingUnit(action.housingUnit.id),
				]);
				return throwError(() => error);
			})
		);
	}

	@Action(SetHousingUnit)
	setHousingUnit(
		ctx: StateContext<HousingStateModel>,
		action: SetHousingUnit
	) {
		ctx.patchState({
			selectedHousingUnit: action.housingUnit,
		});
	}

	@Action(ArchiveHousingUnit)
	archiveHousingUnit(
		ctx: StateContext<HousingStateModel>,
		action: ArchiveHousingUnit
	) {
		ctx.dispatch(new StartLoading());
		return this.housingBackend
			.archiveHousingUnit(action.housingUnitId)
			.pipe(
				tap((housingUnit) =>
					ctx.dispatch([
						new SetHousingUnit(housingUnit),
						new StopLoading(),
					])
				),
				catchError((_error) => {
					ctx.dispatch([new StopLoading()]);
					return of({});
				})
			);
	}

	@Action(ActivateHousingUnit)
	activateHousingUnit(
		ctx: StateContext<HousingStateModel>,
		action: ActivateHousingUnit
	) {
		ctx.dispatch(new StartLoading());
		return this.housingBackend
			.activateHousingUnit(action.housingUnitId)
			.pipe(
				tap((housingUnit) =>
					ctx.dispatch([
						new SetHousingUnit(housingUnit),
						new StopLoading(),
					])
				),
				catchError((_error) => {
					ctx.dispatch([new StopLoading()]);
					return of({});
				})
			);
	}

	@Action(Logout)
	logout(ctx: StateContext<HousingStateModel>, _action: Logout) {
		ctx.patchState(DEFAULT_STATE);
	}
}
